import { useCallback, useEffect, useRef, useState } from 'react';
import { notify } from '../../../notifications';
import { mathErrorMessage } from '../report/mathReportApi';
import type { MathColumn } from '../report/mathReportApi';
import { MATH_FIELDS_API } from './fieldsApi';
import type { FieldsApi } from './fieldsApi';

export type FieldAction = 'save' | 'delete';

/**
 * Данные экрана полей мат. отчета: список, добавление, правка и удаление.
 * api - набор вызовов экрана; он должен быть константой модуля (смена api перезагружает список).
 */
export function useMathFields(api: FieldsApi = MATH_FIELDS_API) {
  const [fields, setFields] = useState<MathColumn[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<FieldAction | null>(null);

  const requestRef = useRef(0);
  const pendingRef = useRef(false);

  /** keepRows: после действия на экране остаются прежние строки (приглушенные), а не пустой экран */
  const load = useCallback(async (keepRows: boolean) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');
    if (!keepRows) setFields(null);

    try {
      const list = await api.list();
      // Устаревший ответ (экран закрыт или список уже перечитан) отбрасывается
      if (requestId !== requestRef.current) return;
      setFields(list);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      const reason = mathErrorMessage(err, 'Проверьте соединение и повторите попытку');
      if (keepRows) notify(`Не удалось обновить список полей. ${reason}`, 'error');
      else setError(reason);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  const retry = useCallback(() => {
    load(false);
  }, [load]);

  /** Выполняет изменение и перечитывает список: после сбоя состояние на сервере могло измениться */
  const run = useCallback(
    async (action: FieldAction, task: () => Promise<string | void>, doneText: string, failText: string): Promise<boolean> => {
      if (pendingRef.current) return false;
      pendingRef.current = true;
      setPending(action);

      let done = false;
      try {
        const warning = await task();
        // Поле сохранено, но с оговоркой (нет колонки в таблице отчета): показываем ее вместо «успеха»
        if (warning) notify(warning, 'warning');
        else notify(doneText, 'success');
        done = true;
      } catch (err) {
        notify(mathErrorMessage(err, failText), 'error');
      } finally {
        pendingRef.current = false;
        setPending(null);
      }

      load(true);
      return done;
    },
    [load]
  );

  const saveField = useCallback(
    (draft: MathColumn, isEditing: boolean) =>
      isEditing
        ? run('save', () => api.update(draft), `Поле «${draft.headerName.trim()}» успешно обновлено`, 'Не удалось сохранить поле')
        : run('save', () => api.create(draft), `Поле «${draft.headerName.trim()}» успешно добавлено`, 'Не удалось добавить поле'),
    [run, api]
  );

  const removeField = useCallback(
    (field: MathColumn) =>
      run('delete', () => api.remove(field.field), `Поле «${field.headerName}» удалено`, 'Не удалось удалить поле'),
    [run, api]
  );

  return { fields, loading, error, retry, pending, saveField, removeField };
}
